import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { ArrowRight, KeyRound, BedDouble, Receipt, MessageSquare, Bell, Sparkles, FileText } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * Operations storytelling — narrative timeline of a guest day.
 */
export default function OperationsStory() {
  const t = useT();

  const STEPS = [
    {
      time: "07:30",
      title: t("Pastrueset marrin listën e ditës", "Housekeepers get the day's list"),
      body: t(
        "Çdo pastruese sheh në telefon vetëm dhomat që duhen përgatitur sot. Statusi përditësohet me një prekje, dhe recepsioni e di menjëherë kur dhoma është gati për klientin tjetër.",
        "Each housekeeper sees only the rooms that need turning today, straight on their phone. Status updates with one tap, so the front desk knows the moment a room is ready for the next guest."
      ),
      icon: BedDouble,
      color: "#0EA5E9",
      visual: (
        <div className="space-y-1.5 text-[11.5px]">
          {[
            ["101", t("E pastruar",   "Cleaned"),  "#DCFCE7", "#166534"],
            ["102", t("Në pastrim",   "Cleaning"), "#FEF3C7", "#92400E"],
            ["103", t("Për pastrim",  "To clean"), "#FEE2E2", "#991B1B"],
          ].map(([n, s, bg, c]) => (
            <div key={n} className="flex items-center justify-between rounded-md border border-line p-1.5">
              <span className="font-bold text-ink">{t("Dhoma", "Room")} {n}</span>
              <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: bg, color: c }}>{s}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      time: "14:00",
      title: t("Klienti bën check-in online para se të mbërrijë", "Guests check in online before they arrive"),
      body: t(
        "Plotëson të dhënat dhe nënshkruan nga telefoni. Kur vjen, recepsioni e pret me çelësin gati — pa rradhë dhe pa letra.",
        "They fill in their details and sign from their phone. When they arrive, the front desk is ready with the key — no queues, no paperwork."
      ),
      icon: KeyRound,
      color: "#FF7A00",
      visual: (
        <div className="rounded-md border border-line p-3 text-[11.5px]">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-ink">A. Krasniqi</div>
              <div className="text-[10px] text-slate3">{t("Dhoma 201 · 3 net", "Room 201 · 3 nights")}</div>
            </div>
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
              {t("Verifikuar", "Verified")}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[9.5px]">
            {[t("Dokumenti","ID"), t("Email","Email"), t("Nënshkrimi","Signature")].map((tt) => (
              <span key={tt} className="rounded bg-[#F1F5F9] px-1 py-1 text-slate2">{tt} ✓</span>
            ))}
          </div>
        </div>
      ),
    },
    {
      time: "18:45",
      title: t("Porosia në bar shkon në llogarinë e dhomës", "The bar order lands on the room bill"),
      body: t(
        "POS-i i barit dhe restorantit shkruan vetë në llogarinë e klientit. Asgjë nuk harrohet. Asnjë mbledhje me dorë në fund të qëndrimit.",
        "The bar and restaurant POS post the charges straight onto the guest folio. Nothing gets forgotten. No manual totalling at check-out."
      ),
      icon: Receipt,
      color: "#16A34A",
      visual: (
        <div className="rounded-md border border-line p-3 text-[11.5px]">
          {[
            [t("Birrë Tirana x2", "Tirana Beer x2"), "€6.00"],
            [t("Sallatë Greke",   "Greek Salad"),    "€6.50"],
            [t("Verë e kuqe",     "Red Wine"),       "€18.00"],
          ].map(([n, p]) => (
            <div key={n} className="flex items-center justify-between border-b border-dashed border-line py-1 last:border-b-0">
              <span className="text-slate2">{n}</span>
              <span className="font-display font-bold text-ink">{p}</span>
            </div>
          ))}
          <div className="mt-2 flex items-center justify-between border-t border-line pt-1.5">
            <span className="text-slate3">→ {t("Dhoma 201", "Room 201")}</span>
            <span className="font-display text-[13px] font-extrabold text-ink">€30.50</span>
          </div>
        </div>
      ),
    },
    {
      time: "21:10",
      title: t("Klienti pyet diçka në WhatsApp", "A guest messages on WhatsApp"),
      body: t(
        "Akomoda i përgjigjet me përgjigje të gatshme që ju i keni krijuar — ose ja kalon recepsionit kur duhet një njeri pas mesazhit.",
        "Akomoda replies with the templates you've set up — or hands it over to the front desk when the message needs a human."
      ),
      icon: MessageSquare,
      color: "#25D366",
      visual: (
        <div className="space-y-1.5 text-[11.5px]">
          <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-sm bg-[#DCFCE7] px-3 py-2 text-[#166534]">
            {t("A mund të rezervoni dy persona për mëngjes?", "Can you book breakfast for two, please?")}
          </div>
          <div className="max-w-[80%] rounded-2xl rounded-bl-sm border border-line bg-white px-3 py-2 text-slate2">
            {t(
              <>Sigurisht, z. Krasniqi! Mëngjesi servohet 07:00–10:30. Tavolinë për 2 në orën 08:30? <span className="font-semibold text-ink">[Po]</span></>,
              <>Of course, Mr Krasniqi! Breakfast is served 07:00–10:30. Table for 2 at 08:30? <span className="font-semibold text-ink">[Yes]</span></>
            )}
          </div>
        </div>
      ),
    },
    {
      time: "11:20",
      title: t("Check-out dhe faturë automatike", "Check-out and automatic invoicing"),
      body: t(
        "Akomodimi, porositë në bar dhe shërbimet shtesë mblidhen në një faturë. Fiskalizimi bëhet vetë. PDF-ja i dërgohet klientit me email.",
        "Accommodation, bar orders and extras merge into a single invoice. Fiscalization runs on its own. The PDF is emailed to the guest."
      ),
      icon: FileText,
      color: "#8B5CF6",
      visual: (
        <div className="rounded-md border border-line p-3 text-[11.5px]">
          <div className="flex items-center justify-between">
            <span className="font-display font-bold text-ink">{t("Faturë", "Invoice")} #2026-0184</span>
            <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
              {t("Fiskalizuar", "Fiscalized")}
            </span>
          </div>
          <div className="mt-2 space-y-0.5 text-[11px] text-slate2">
            <div className="flex justify-between"><span>{t("Akomodim 3 net", "3-night stay")}</span><span>€276.00</span></div>
            <div className="flex justify-between"><span>{t("Bar dhe restorant", "Bar & restaurant")}</span><span>€48.50</span></div>
            <div className="flex justify-between"><span>{t("TVSh", "VAT")}</span><span>€48.75</span></div>
          </div>
          <div className="mt-2 flex justify-between border-t border-line pt-1.5">
            <span className="font-semibold text-ink">{t("Totali", "Total")}</span>
            <span className="font-display text-[13px] font-extrabold text-ink">€373.25</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="bg-surface-1">
      <div className="container-akm section">
        <div className="grid items-end gap-6 md:grid-cols-12">
          <div className="md:col-span-7">
            <div className="eyebrow">{t("Operacionet", "Operations")}</div>
            <h2 className="h2-akm mt-3">
              {t("Një ditë normale në hotel — pa boshllëqe.", "A normal day at the hotel — with no gaps.")}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="lead">
              {t(
                "Akomoda lidh çdo veprim që bën ekipi i hotelit — nga pastrimi në mëngjes, te porosia në bar, te check-out në mëngjesin tjetër. Asnjë gjë nuk humbet rrugës.",
                "Akomoda ties together every action your team takes — from the morning turnaround to the bar order to next morning's check-out. Nothing slips through."
              )}
            </p>
          </div>
        </div>

        <div className="relative mt-14">
          <div className="absolute left-[78px] top-0 hidden h-full w-px bg-gradient-to-b from-transparent via-line to-transparent md:block" aria-hidden />

          <ol className="space-y-10 md:space-y-14">
            {STEPS.map(({ time, title, body, icon: Icon, color, visual }, i) => (
              <li key={time} className="relative grid items-start gap-4 md:grid-cols-[80px_1fr]">
                <div className="flex items-center gap-3 md:flex-col md:items-end md:gap-2 md:pr-4">
                  <span className="font-mono text-[12.5px] font-semibold tracking-tight text-slate3 md:order-2">{time}</span>
                  <span
                    className="relative grid h-9 w-9 place-items-center rounded-full border border-line bg-white md:order-1"
                    style={{ color }}
                  >
                    <Icon size={15} />
                    <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full ring-2 ring-white" style={{ background: color }} />
                  </span>
                </div>
                <div className="grid items-start gap-5 md:grid-cols-2">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate3">
                      {t("Hapi", "Step")} {i + 1}
                    </div>
                    <h3 className="mt-1 font-display text-[20px] font-bold tracking-[-0.02em] text-ink">{title}</h3>
                    <p className="mt-2 text-[14.5px] leading-[1.65] text-slate2">{body}</p>
                  </div>
                  <div className="card-elevated p-4">{visual}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Link to="/platforma" className="btn-primary">
            {t("Shiko të gjithë Platformën", "See the whole platform")} <ArrowRight size={14} />
          </Link>
          <Link to="/integrimet" className="btn-ghost">{t("Të gjitha integrimet →", "All integrations →")}</Link>
        </div>
      </div>
    </section>
  );
}
